import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const PHASES = [
  { id: "discover", label: "Discover", range: "01–04", product: "SimMarket", start: 0 },
  { id: "trust", label: "Trust", range: "05–06", product: "SimVerify", start: 4 },
  { id: "sustain", label: "Sustain", range: "07–08", product: "SimCollect", start: 6 },
];

const STEPS = [
  {
    title: "Learn",
    copy: "Consumers access educational financial content and expert guidance.",
    product: "SimMarket",
    href: "#simmarket",
    phase: 0,
    beats: ["Education", "Guidance", "Clarity"],
  },
  {
    title: "Compare",
    copy: "Products and providers are compared side by side.",
    product: "SimMarket",
    href: "#simmarket",
    phase: 0,
    beats: ["Products", "Providers", "Fit"],
  },
  {
    title: "AI Recommendations",
    copy: "Our intelligent engine recommends the most suitable financial products.",
    product: "SimMarket",
    href: "#simmarket",
    phase: 0,
    beats: ["Signals", "Match", "Recommend"],
  },
  {
    title: "Connect",
    copy: "Qualified customers connect with verified financial institutions.",
    product: "SimMarket",
    href: "#simmarket",
    phase: 0,
    beats: ["Qualified", "Institution", "Handoff"],
  },
  {
    title: "Verify",
    copy: "Financial institutions use SimVerify to establish digital trust through borrower, vehicle, device and behavioural verification.",
    product: "SimVerify",
    href: "/simverify",
    phase: 1,
    beats: ["Borrower", "Vehicle", "Device"],
  },
  {
    title: "Approve",
    copy: "Institutions make faster and more informed lending decisions.",
    product: "SimVerify",
    href: "/simverify",
    phase: 1,
    beats: ["Score", "Decision", "Faster"],
  },
  {
    title: "Engage",
    copy: "Customers receive continuous engagement and support.",
    product: "SimCollect",
    href: "/simcollect",
    phase: 2,
    beats: ["Support", "Updates", "Retention"],
  },
  {
    title: "Recover",
    copy: "SimCollect automates repayments, customer engagement and debt recovery when required.",
    product: "SimCollect",
    href: "/simcollect",
    phase: 2,
    beats: ["Portfolio", "Repay", "Recovered"],
  },
];

function scrollToStep(index) {
  const node = document.getElementById(`how-step-${String(index + 1).padStart(2, "0")}`);
  node?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function How() {
  const reduce = useReducedMotion();
  const [progress, setProgress] = useState(0);
  const phase = PHASES[STEPS[progress].phase];

  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="how"
      className="relative overflow-hidden bg-[#f3f7f5] text-navy"
      aria-labelledby="how-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_50%_0%,rgba(229,184,11,0.14),transparent_55%),radial-gradient(55%_40%_at_50%_100%,rgba(12,31,27,0.08),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.38]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="mx-auto max-w-3xl text-center"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            How SimFinance works
          </p>
          <h2
            id="how-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            One intelligent
            <span className="mt-1 block">customer journey</span>
          </h2>
        </motion.header>

        <motion.div
          className="mx-auto mt-10 grid max-w-3xl gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-3"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          {PHASES.map((item) => {
            const current = item.id === phase.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToStep(item.start)}
                className={`flex min-h-16 cursor-pointer flex-col justify-center px-5 py-4 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold ${
                  current ? "bg-[#f7faf3]" : "bg-white hover:bg-[#f3f7f5]"
                }`}
                aria-current={current ? "true" : undefined}
              >
                <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                  {item.range}
                </span>
                <span className="mt-1.5 text-sm font-semibold tracking-tight text-navy">
                  {item.label}
                </span>
                <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/35">
                  {item.product}
                </span>
              </button>
            );
          })}
        </motion.div>

        <ol className="relative mx-auto mt-16 max-w-5xl" aria-label="Customer journey steps">
          <div
            className="pointer-events-none absolute bottom-10 left-6 top-10 w-px -translate-x-1/2 lg:left-1/2"
            aria-hidden="true"
          >
            <span className="absolute inset-0 bg-navy/10" />
            <motion.span
              className="absolute inset-x-0 top-0 origin-top bg-gradient-to-b from-gold via-royal to-gold"
              initial={false}
              animate={{ scaleY: (progress + 1) / STEPS.length }}
              transition={reduce ? { duration: 0 } : { duration: 0.55, ease: EASE }}
              style={{ height: "100%" }}
            />
          </div>

          {STEPS.map((item, index) => {
            const selected = index === progress;
            const passed = index < progress;
            const isLeft = index % 2 === 0;
            const number = String(index + 1).padStart(2, "0");

            const card = (
              <article
                id={`how-step-${number}`}
                className={`relative w-full max-w-md scroll-mt-32 border bg-white px-5 py-5 sm:px-6 ${
                  selected
                    ? "border-gold/60 shadow-[0_28px_60px_-32px_rgba(12,31,27,0.45)]"
                    : passed
                      ? "border-navy/15"
                      : "border-navy/10"
                } ${isLeft ? "lg:text-right" : ""}`}
              >
                <p className={`flex items-center gap-2 ${isLeft ? "lg:flex-row-reverse" : ""}`}>
                  <span
                    className={`font-mono text-[11px] font-semibold tracking-[0.2em] ${
                      selected ? "text-gold" : "text-navy/30"
                    }`}
                  >
                    {number}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/35">
                    {item.product}
                  </span>
                </p>
                <h3
                  className={`mt-2 text-xl font-semibold tracking-tight ${
                    selected ? "text-navy" : "text-navy/75"
                  }`}
                >
                  {item.title}
                </h3>
                <p
                  className={`mt-2 text-sm leading-relaxed ${
                    selected ? "text-navy/65" : "text-navy/50"
                  }`}
                >
                  {item.copy}
                </p>
                <ul
                  className={`mt-4 flex flex-wrap gap-1.5 ${isLeft ? "lg:justify-end" : ""}`}
                >
                  {item.beats.map((beat) => (
                    <li
                      key={beat}
                      className={`border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                        selected
                          ? "border-gold/40 bg-[#f7faf3] text-navy/70"
                          : "border-navy/10 text-navy/45"
                      }`}
                    >
                      {beat}
                    </li>
                  ))}
                </ul>
                <a
                  href={item.href}
                  className={`mt-4 inline-flex min-h-10 items-center gap-1.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
                    selected ? "text-navy" : "text-navy/45 hover:text-navy"
                  }`}
                >
                  Continue in {item.product}
                  <ArrowIcon />
                </a>
              </article>
            );

            return (
              <motion.li
                key={item.title}
                className="relative grid grid-cols-[3rem_minmax(0,1fr)] items-center py-5 lg:grid-cols-[minmax(0,1fr)_5.5rem_minmax(0,1fr)] lg:py-6"
                initial={
                  reduce
                    ? { opacity: 1, x: 0, y: 0 }
                    : { opacity: 0, x: isLeft ? -40 : 40, y: 24 }
                }
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.4, margin: "-8% 0px -12% 0px" }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : { duration: 0.6, delay: 0.04, ease: EASE }
                }
                onViewportEnter={() =>
                  setProgress((current) => Math.max(current, index))
                }
              >
                {isLeft ? (
                  <div className="order-2 flex items-center lg:order-1 lg:justify-end">
                    {card}
                    <span
                      className={`hidden h-px w-6 shrink-0 lg:block ${
                        selected ? "bg-gold" : passed ? "bg-royal/50" : "bg-navy/15"
                      }`}
                      aria-hidden="true"
                    />
                  </div>
                ) : (
                  <div className="order-1 hidden lg:block" />
                )}

                <div className="relative z-[1] order-1 flex justify-center lg:order-2">
                  <span
                    className={`relative flex h-11 w-11 items-center justify-center rounded-full border-2 font-mono text-[11px] font-semibold tracking-[0.12em] ${
                      selected
                        ? "border-gold bg-gold text-navy"
                        : passed
                          ? "border-royal bg-navy text-white"
                          : "border-navy/15 bg-white text-navy/35"
                    }`}
                  >
                    {selected && !reduce ? (
                      <motion.span
                        className="absolute inset-[-6px] rounded-full border border-gold/40"
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.45, ease: EASE }}
                      />
                    ) : null}
                    {number}
                  </span>
                </div>

                {!isLeft ? (
                  <div className="order-2 flex items-center lg:order-3">
                    <span
                      className={`hidden h-px w-6 shrink-0 lg:block ${
                        selected ? "bg-gold" : passed ? "bg-royal/50" : "bg-navy/15"
                      }`}
                      aria-hidden="true"
                    />
                    {card}
                  </div>
                ) : (
                  <div className="order-3 hidden lg:block" />
                )}
              </motion.li>
            );
          })}
        </ol>

        <motion.p
          className="mx-auto mt-16 max-w-3xl text-center text-base leading-relaxed text-navy/60 sm:text-lg"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          This integrated journey creates a seamless, intelligent financial ecosystem that benefits
          consumers, financial institutions and the wider economy.
        </motion.p>
      </div>
    </section>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default How;
